/* eslint-disable lingui/no-unlocalized-strings --
 * Nothing in this file is user-facing copy. Event names and property keys are
 * wire identifiers sent to Mixpanel; translating them would split one metric
 * into one series per locale.
 */
import mixpanel from "mixpanel-browser";

import { env } from "@/client/config/env";

import { getIncomingDevice } from "./attribution";
import { analyticsAllowed, onConsentChange } from "./consent";
import { isTrackedEnvironment, resolveEnvironment, safely } from "./environment";
import { resolveSite } from "./site";

/**
 * Every event the client is allowed to send.
 *
 * Mixpanel enforces no schema of its own, so this union is what stops names from
 * drifting: a typo becomes a build error, and names can never be assembled at
 * runtime. Adding an event means adding it here first.
 */
export const ANALYTICS_EVENTS = [
  // Funnel events shared with GA4 (see analytics/track.ts). page_viewed is sent as Mixpanel's
  // native $mp_web_page_view instead, so the built-in page reports keep working.
  "cta_clicked",
  "trial_started",
  "trial_completed",
  "signup_completed",
  "checkout_started",
  "plan_dialog_opened",
  "plan_selected",
  "coupon_applied",
  "coupon_redeemed",
  "coupon_rejected",
  "video_chapter_viewed",
  "video_loop_completed",
  "video_sound_opened",
  "video_progress",
  "video_closed",

  // Naming (decided 2026-10-21): object_action, snake_case, past tense. Mixpanel-only from here.

  // Pages and identity. page_exited: one per page view, with the seconds it was visible.
  "page_exited",
  "section_viewed",
  "faq_opened",
  // The sample review on the First Round home page: a chapter came up, or someone clicked into it.
  "showcase_chapter_viewed",
  "showcase_interacted",
  "signed_in",
  "signed_out",
  // Carries the other site's $device_id alongside the user id, so Mixpanel merges the visitor's
  // activity there into their profile (see linkDevice).
  "identity_linked",

  // Plans and checkout. The paid moment itself (payment_completed) is sent by the server.
  "plan_dialog_dismissed",
  "checkout_cancelled",
  "checkout_returned",

  // The free trial demo on firstround.com.au (anonymous) and its results page.
  "demo_topic_selected",
  "demo_failed",
  "demo_results_gate_shown",
  "demo_results_auth_clicked",
  "demo_results_opened",
  "demo_last_results_opened",
  // The interviewer's reply time in a demo call, measured in the visitor's browser.
  "demo_reply_measured",
  // A "Not available in the trial · Get Starter" lock (feature: what it stood in for).
  "demo_upsell_clicked",

  // Practice interviews in the app. practice_completed is sent by the server at session end.
  "practice_created",
  "practice_started",
  "practice_start_failed",
  "practice_first_audio",
  "practice_feedback_viewed",
  "practice_review_played",
  "practice_drill_started",
  "practice_drill_completed",

  // Resumes.
  "resume_created",
  "resume_imported",
  "resume_edited",
  "resume_ai_edited",
  "resume_downloaded",
  "job_description_added",

  // Industry pages (already snake_case before 2026-10-21; names kept).
  "institutions_page_view",
  "institutions_segment_switch",
  "institutions_cta_click",
  "institutions_scroll_depth",

  // Modules hidden from the menu at launch.
  "career_quest_paths_generated",
  "career_quest_path_opened",
  "career_quest_stage_submitted",
  "career_quest_path_completed",
  "interview_started",
  "interview_start_failed",
  "interview_completed",
  "job_searched",
  "job_search_failed",
  "spotlight_intro_started",
  "spotlight_start_failed",
  "spotlight_intro_completed",
] as const;

export type AnalyticsEvent = (typeof ANALYTICS_EVENTS)[number];

type Properties = Record<string, unknown>;

type IdentifiableUser = {
  id: string;
  name?: string;
  email?: string;
  locale?: string;
  provider?: string;
  emailVerified?: boolean;
  linkedinUrl?: string | null;
  profileCompletedAt?: Date | string | null;
  createdAt?: Date | string;
};

let initialized = false;
// init() has run on this page (initialized goes false again while opted out).
let loadedOnce = false;

// Text inside these stays readable in session replays (see initMixpanel). The recorder matches
// with closest(), so marking a page's root is enough.
export const REPLAY_UNMASKED_TEXT = ".marketing-page, .fr-page, .pi-page, [data-mp-unmask]";

/**
 * Link to this visit's Mixpanel session replay, or undefined when Mixpanel isn't running (not a
 * tracked environment, blocked by an ad blocker) or hasn't started recording. Stored on the CRM
 * lead so the team can watch how someone used the demo.
 */
export const currentReplayUrl = (): string | undefined => {
  if (!initialized) return undefined;
  try {
    return mixpanel.get_session_replay_url() || undefined;
  } catch {
    return undefined;
  }
};

// Set by the visitor's behaviour and kept for every later event in this browser, so any report
// can compare people who did something with people who didn't (cohorts can't be saved on Free).
const BEHAVIOUR_FLAGS = ["watched_film", "tried_demo"] as const;
type BehaviourFlag = (typeof BEHAVIOUR_FLAGS)[number];

/** Properties on every event from this browser. Registered again after reset(). */
const registerBase = () => {
  mixpanel.register({
    environment: resolveEnvironment(),
    platform: "web",
    site: resolveSite(window.location.hostname),
  });
  // false until it happens, so breakdowns show false instead of "(not set)".
  mixpanel.register_once(Object.fromEntries(BEHAVIOUR_FLAGS.map((flag) => [flag, false])));
};

/**
 * firstround.com.au and theresume.io keep separate storage, so a visitor gets a new anonymous id
 * on each, and anyone who doesn't sign up counted as two people in every cross-site funnel. When a
 * link from the other site hands over its device id and nobody is signed in here, carry on as that
 * same device: the same reset-and-register the SDK's own reset() does, with the handed-over id.
 * Simplified ID merge (confirmed on project 3978958) then joins everything at sign-up.
 * This browser's earlier anonymous history here, if any, stays under the old id.
 */
const adoptDevice = (handedOver: string | undefined) => {
  const deviceId = handedOver?.replace(/^\$device:/, "");
  if (!deviceId) return;
  if (mixpanel.get_property("$user_id")) return;
  if (mixpanel.get_property("$device_id") === deviceId) return;

  const flags = Object.fromEntries(
    BEHAVIOUR_FLAGS.map((flag) => [flag, mixpanel.get_property(flag) === true]),
  );
  mixpanel.reset();
  mixpanel.register({ distinct_id: `$device:${deviceId}`, $device_id: deviceId });
  registerBase();
  mixpanel.register(flags);
};

// Every route the SDK calls: a custom api_routes replaces the defaults instead of merging, and
// the SDK's types leave out "settings". The server maps them back (mixpanel-proxy.ts).
const API_ROUTES = {
  track: "e/",
  engage: "p/",
  groups: "g/",
  record: "r/",
  flags: "f/",
  settings: "s/",
};

const start = () => {
  safely("init", () => {
    mixpanel.init(env.VITE_MIXPANEL_TOKEN, {
      debug: env.DEV,
      persistence: "localStorage",
      // Through our own host (server analytics/mixpanel-proxy.ts), so ad blockers that drop
      // api-js.mixpanel.com don't hide the visitor. Short route names for the same reason.
      api_host: `${window.location.origin}/api/t`,
      api_routes: API_ROUTES,
      // Clicks, inputs, scroll depth and form submits stay on at their defaults (kept on
      // 2026-10-21); only autocapture's own page views are disabled. `autocapture: true`
      // would enable them at "full-url", which both duplicates the page view
      // AnalyticsTracker sends and fires again on every `#anchor` on the
      // marketing pages. Passing an object merges over the SDK's defaults.
      autocapture: { pageview: false },
      // Page views are sent by AnalyticsTracker instead, so each one carries a
      // normalised route (/dashboard/interview/:interviewId) rather than a URL
      // holding a different ID for every candidate. Note this flag alone is not
      // enough — autocapture overrides it, hence the object above.
      track_pageview: false,
      // Session replay, on for every session (decided 2026-09-25; analytics and replay on
      // everywhere, decided 2026-10-21). Privacy rules:
      //   - Inputs are always masked, and images, video and audio are blocked
      //     (the SDK defaults), so camera self-views never leave the browser.
      //   - Rendered text is masked everywhere except the marketing surfaces
      //     and the trial: the First Round and theResume marketing pages, the
      //     practice demo (its live captions stay readable, by choice), plus
      //     anything marked [data-mp-unmask], such as the demo results page.
      //     The builder, dashboard, resumes and paid interviews stay masked.
      record_sessions_percent: 100,
      record_mask_all_text: true,
      record_unmask_text_selector: REPLAY_UNMASKED_TEXT,
    });

    // Someone who opted out and later opted back in (here or on the other site).
    if (mixpanel.has_opted_out_tracking()) mixpanel.opt_in_tracking();

    initialized = true;
    loadedOnce = true;
    adoptDevice(getIncomingDevice());
    registerBase();
  });
};

let consentWatched = false;

/** Cookie settings: off stops events and replay at once; on starts them again. */
const watchConsent = () => {
  if (consentWatched) return;
  consentWatched = true;
  onConsentChange((consent) => {
    if (consent === "denied") {
      if (!initialized) return;
      safely("opt out", () => {
        mixpanel.stop_session_recording();
        mixpanel.opt_out_tracking();
      });
      initialized = false;
      return;
    }
    if (initialized) return;
    // Already loaded once on this page: opting in again keeps the same SDK instance.
    if (loadedOnce) {
      safely("opt in", () => {
        mixpanel.opt_in_tracking();
        mixpanel.start_session_recording();
        initialized = true;
        registerBase();
      });
    } else {
      start();
    }
  });
};

export const initMixpanel = () => {
  if (initialized) return;

  const environment = resolveEnvironment();

  if (!isTrackedEnvironment(environment)) {
    if (env.DEV) console.info(`[analytics] disabled in ${environment}`);
    return;
  }

  watchConsent();
  if (!analyticsAllowed()) return;
  start();
};

/** Turns a behaviour flag on for every later event from this browser (see BEHAVIOUR_FLAGS). */
export const markBehaviour = (flag: BehaviourFlag) => {
  if (!initialized) return;
  safely("mark behaviour", () => mixpanel.register({ [flag]: true }));
};

/** Who is using the app right now, on every event: signed in or not, and their plan. */
export const registerUserState = (state: {
  signed_in: boolean;
  plan?: string;
  pass_status?: string;
}) => {
  if (!initialized) return;
  safely("register user state", () => {
    mixpanel.register(state);
    if (!state.signed_in) mixpanel.unregister("plan");
    if (!state.signed_in) mixpanel.unregister("pass_status");
  });
};

/** The current page on every event, so a click or an error says where it happened. */
export const registerPage = (page: { page_route: string; page_name: string }) => {
  if (!initialized) return;
  safely("register page", () => mixpanel.register(page));
};

export const identifyUser = (user: IdentifiableUser, extras?: Properties) => {
  if (!initialized) return;

  safely("identify", () => {
    mixpanel.identify(user.id);

    mixpanel.people.set({
      $name: user.name,
      $email: user.email,
      locale: user.locale,
      provider: user.provider,
      email_verified: user.emailVerified,
      profile_completed: Boolean(user.profileCompletedAt),
      has_linkedin: Boolean(user.linkedinUrl),
      ...extras,
    });

    // Never overwritten on later logins — this is what makes signup-cohort
    // retention reports possible.
    if (user.createdAt) mixpanel.people.set_once({ $created: user.createdAt });
  });
};

// The last Clerk session seen in this browser: a new one means someone just signed in.
const SESSION_KEY = "analytics_clerk_session";

/**
 * signed_in, once per Clerk session (sign-up creates a session too: new_account tells them apart).
 * Page loads within the same session don't count. The first load after this shipped counts every
 * open session once.
 */
export const trackSignIn = (sessionId: string, user: IdentifiableUser) => {
  if (!initialized) return;
  try {
    if (localStorage.getItem(SESSION_KEY) === sessionId) return;
    localStorage.setItem(SESSION_KEY, sessionId);
  } catch {
    return; // Without storage every page load would look like a sign-in.
  }
  identifyUser(user);
  track("signed_in", { method: user.provider, new_account: !user.profileCompletedAt });
};

/** Before resetAnalytics(), so the event still belongs to the person signing out. */
export const trackSignOut = () => {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // Nothing to clear.
  }
  track("signed_out");
};

/**
 * Clears the identity so the next person to use the browser does not inherit the
 * previous one's distinct_id.
 */
export const resetAnalytics = () => {
  if (!initialized) return;

  safely("reset", () => {
    const flags = Object.fromEntries(
      BEHAVIOUR_FLAGS.map((flag) => [flag, mixpanel.get_property(flag) === true]),
    );
    mixpanel.reset();
    // reset() clears every super property, including the ones each event must carry.
    registerBase();
    mixpanel.register({ ...flags, signed_in: false });
  });
};

export const track = (event: AnalyticsEvent, properties?: Properties) => {
  if (!initialized) return;

  safely(`track ${event}`, () => {
    mixpanel.track(event, properties);
  });
};

/**
 * Sends Mixpanel's native `$mp_web_page_view`, so the built-in page reports keep
 * working, with the normalised route attached for grouping.
 */
export const trackPageVisit = (properties: Properties) => {
  if (!initialized) return;

  safely("track_pageview", () => {
    mixpanel.track_pageview(properties);
  });
};

/** Properties sent with every later event from this browser (e.g. first-touch attribution). */
export const registerSuperProperties = (properties: Properties) => {
  if (!initialized) return;

  safely("register", () => {
    mixpanel.register(properties);
  });
};

/** Profile properties that must never be overwritten once set (e.g. first-touch attribution). */
export const setPersonOnce = (properties: Properties) => {
  if (!initialized) return;

  safely("people.set_once", () => {
    mixpanel.people.set_once(properties);
  });
};

/** Adds to a counter on the signed-in person's profile (e.g. resumes_created_count). */
export const incrementPerson = (property: string, by = 1) => {
  if (!initialized) return;

  safely("people.increment", () => {
    mixpanel.people.increment(property, by);
  });
};

/** This browser's anonymous device id, handed to the other site on links (see attribution.ts). */
export const getDeviceId = (): string | undefined => {
  if (!initialized) return undefined;

  try {
    const id = mixpanel.get_property("$device_id");
    return typeof id === "string" ? id : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Merge a device id seen on the other site into the identified user.
 * Under Mixpanel's simplified ID merge, one event carrying both $device_id and $user_id links
 * them, so the marketing-site events join this user's profile. Call after identifyUser.
 */
export const linkDevice = (deviceId: string) => {
  if (!initialized) return;

  safely("link device", () => {
    mixpanel.track("identity_linked", { $device_id: deviceId.replace(/^\$device:/, "") });
  });
};
